/**
 * Utility functions for cleaning up corrupted localStorage data
 */

import { LOCAL_STORAGE_KEYS } from '../constants/localStorageKeys';
import { uiLogger } from './loggerInstance';

/**
 * Clean up known problematic or legacy localStorage keys
 * This should be called once during app initialization to fix legacy data
 */
export function cleanupCorruptedLocalStorage(): void {
  // 1. Definitively remove legacy selection keys to prevent interference with current state management
  // These keys are now managed in global store (chatStore)
  const legacyKeysToRemove = [
    LOCAL_STORAGE_KEYS.SELECTED_MODEL,
    LOCAL_STORAGE_KEYS.SELECTED_INDUSTRY,
  ];

  legacyKeysToRemove.forEach((key) => {
    if (localStorage.getItem(key)) {
      uiLogger.info(`Removing legacy localStorage key: ${key}`);
      localStorage.removeItem(key);
    }
  });

  // 2. Additional corruption checks for other keys can be added here if needed
}

/**
 * Safely get a value from localStorage with fallback handling
 * @param key - The localStorage key
 * @param fallback - Fallback value if key doesn't exist or is corrupted
 * @returns The parsed value or fallback
 */
export function safeGetLocalStorage<T>(key: string, fallback: T): T {
  try {
    const item = localStorage.getItem(key);
    if (!item) {
      return fallback;
    }

    return JSON.parse(item);
  } catch (error) {
    uiLogger.warn('localStorage key is corrupted, using fallback', { key, error });
    localStorage.removeItem(key);
    return fallback;
  }
}

/**
 * Safely set a value in localStorage with JSON serialization
 * @param key - The localStorage key
 * @param value - The value to store
 */
export function safeSetLocalStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    uiLogger.error(`Failed to set localStorage key "${key}"`, error);
  }
}
